import {
  Bell,
  BookOpen,
  Calculator,
  FolderOpen,
  GraduationCap,
  House,
  LayoutDashboard,
  Library,
  Pill,
  Search,
  UserRound,
} from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { useUnreadCount } from '@/features/inbox/api';
import { useAuth } from '@/lib/auth';
import { APP_NAME, isBackendConfigured } from '@/lib/env';
import { cn } from '@/lib/utils';
import { GlobalSearch } from './GlobalSearch';
import { OfflineBanner, UpdatePrompt } from './notices';
import { IconButton, Select, Spinner } from './ui';

const PRIMARY_NAV = [
  { to: '/', label: 'Home', icon: House, end: true },
  { to: '/protocols', label: 'Protocols', icon: BookOpen },
  { to: '/meds', label: 'Meds', icon: Pill },
  { to: '/tools', label: 'Tools', icon: Calculator },
  { to: '/learn', label: 'Learn', icon: GraduationCap },
];

const SECONDARY_NAV = [
  { to: '/library', label: 'State library', icon: Library },
  { to: '/resources', label: 'Resources', icon: FolderOpen },
  { to: '/inbox', label: 'Inbox', icon: Bell },
];

const sideLink = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
    isActive
      ? 'bg-stone-200/70 font-medium text-stone-950 dark:bg-ink-800 dark:text-white'
      : 'text-stone-600 hover:bg-stone-200/40 hover:text-stone-950 dark:text-stone-400 dark:hover:bg-ink-900 dark:hover:text-white',
  );

const topIcon =
  'relative inline-flex size-10 items-center justify-center rounded-md text-stone-500 transition-colors hover:bg-stone-200/60 hover:text-stone-950 dark:text-stone-400 dark:hover:bg-ink-800 dark:hover:text-white';

function OrgSwitcher() {
  const { memberships, activeOrgId, setActiveOrgId } = useAuth();
  if (memberships.length === 0) return null;
  if (memberships.length === 1) {
    return <span className="truncate text-sm font-medium text-stone-700 dark:text-stone-300">{memberships[0]!.organization.name}</span>;
  }
  return (
    <Select
      aria-label="Active organization"
      value={activeOrgId ?? ''}
      onChange={(e) => setActiveOrgId(e.target.value)}
      className="h-9 max-w-[14rem] truncate text-sm"
    >
      {memberships.map((m) => (
        <option key={m.org_id} value={m.org_id}>
          {m.organization.name}
        </option>
      ))}
    </Select>
  );
}

export function AppShell() {
  const { user, isEditor, isPlatformAdmin, activeMembership } = useAuth();
  const unread = useUnreadCount();
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const brandColor = activeMembership?.organization.brand_color;
  const showAdmin = Boolean(user) && (isEditor || isPlatformAdmin);

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[14.5rem_1fr]">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-dvh flex-col border-r border-stone-200 px-3 pb-4 lg:flex dark:border-stone-800">
        <Link to="/" className="flex h-14 items-center gap-2 px-2.5">
          <img src="/icons/icon.svg" alt="" className="size-7 rounded-[7px] dark:ring-1 dark:ring-white/15" />
          <span className="text-[15px] font-semibold tracking-tight">{APP_NAME}</span>
        </Link>
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="mb-4 mt-1 flex h-9 items-center gap-2 rounded-md border border-stone-300 bg-white px-2.5 text-sm text-stone-500 transition-colors hover:border-stone-400 dark:border-stone-700 dark:bg-ink-900 dark:text-stone-400 dark:hover:border-stone-600"
        >
          <Search className="size-4" />
          <span className="flex-1 text-left">Search</span>
          <kbd className="font-mono text-[11px] text-stone-400">Ctrl K</kbd>
        </button>
        <nav className="space-y-0.5" aria-label="Primary">
          {PRIMARY_NAV.map(({ to, label, icon: Icon, ...rest }) => (
            <NavLink key={to} to={to} end={'end' in rest} className={sideLink}>
              <Icon className="size-4 opacity-80" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="label-caps mb-1.5 mt-6 px-2.5 text-stone-400 dark:text-stone-500">Library & updates</div>
        <nav className="space-y-0.5" aria-label="Secondary">
          {SECONDARY_NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={sideLink}>
              <Icon className="size-4 opacity-80" />
              {label}
              {to === '/inbox' && unread > 0 && (
                <span className="ml-auto rounded-[4px] bg-red-600 px-1.5 font-mono text-[11px] font-medium leading-4 text-white">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        {showAdmin && (
          <>
            <div className="label-caps mb-1.5 mt-6 px-2.5 text-stone-400 dark:text-stone-500">Manage</div>
            <nav aria-label="Administration">
              <NavLink to="/admin" className={sideLink}>
                <LayoutDashboard className="size-4 opacity-80" />
                Admin
              </NavLink>
            </nav>
          </>
        )}
        <p className="mt-auto border-t border-stone-200 px-2.5 pt-3 text-xs leading-relaxed text-stone-500 dark:border-stone-800">
          Clinical decision support. Follow your local protocols.
        </p>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header
          className="safe-top sticky top-0 z-40 border-b border-stone-200 bg-stone-50/90 backdrop-blur dark:border-stone-800 dark:bg-ink-950/90"
          style={brandColor ? { boxShadow: `inset 0 3px 0 ${brandColor}` } : undefined}
        >
          <div className="flex h-14 items-center gap-1 px-3 sm:px-4">
            <Link to="/" className="mr-1 flex items-center lg:hidden">
              <img src="/icons/icon.svg" alt="" className="size-7 rounded-[7px] dark:ring-1 dark:ring-white/15" />
              <span className="sr-only">{APP_NAME}</span>
            </Link>
            <div className="min-w-0 flex-1 px-1">
              {user ? <OrgSwitcher /> : <span className="text-[15px] font-semibold tracking-tight lg:hidden">{APP_NAME}</span>}
            </div>
            <IconButton label="Search (Ctrl+K)" onClick={() => setSearchOpen(true)} className="lg:hidden">
              <Search className="size-5" />
            </IconButton>
            {user && (
              <Link to="/inbox" aria-label={`Inbox, ${unread} unread`} className={topIcon}>
                <Bell className="size-5" />
                {unread > 0 && (
                  <span className="absolute right-1.5 top-1.5 min-w-4 rounded-full bg-red-600 px-1 text-center font-mono text-[10px] font-semibold leading-4 text-white">
                    {unread > 9 ? '9+' : unread}
                  </span>
                )}
              </Link>
            )}
            {showAdmin && (
              <Link to="/admin" aria-label="Admin" className={cn(topIcon, 'lg:hidden')}>
                <LayoutDashboard className="size-5" />
              </Link>
            )}
            <Link to={user || !isBackendConfigured ? '/profile' : '/login'} aria-label="Profile" className={topIcon}>
              <UserRound className="size-5" />
            </Link>
          </div>
          <OfflineBanner />
        </header>

        <main className="mx-auto w-full max-w-5xl flex-1 px-4 pb-28 pt-6 sm:px-6 lg:pb-12 lg:pt-8">
          <Suspense fallback={<Spinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <nav
        aria-label="Primary"
        className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-stone-200 bg-stone-50/95 backdrop-blur lg:hidden dark:border-stone-800 dark:bg-ink-950/95"
      >
        <div className="grid grid-cols-5">
          {PRIMARY_NAV.map(({ to, label, icon: Icon, ...rest }) => (
            <NavLink
              key={to}
              to={to}
              end={'end' in rest}
              className={({ isActive }) =>
                cn(
                  'relative flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium',
                  isActive
                    ? 'text-stone-950 before:absolute before:inset-x-5 before:top-0 before:h-0.5 before:bg-stone-900 dark:text-white dark:before:bg-stone-100'
                    : 'text-stone-500',
                )
              }
            >
              <Icon className="size-[22px]" strokeWidth={1.75} />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <UpdatePrompt />
    </div>
  );
}
